<script setup lang="ts">
import { nextTick, onMounted, ref } from "vue";
import ScrollTrigger from "gsap/ScrollTrigger";
interface KirbyImage {
  id: string;
  uuid: string;
  url: string;
  alt: string;
}
const props = defineProps<{
  block?: Record<string, any>;
}>();

function generateUniqueId() {
  return `video-${Math.random().toString(36).substr(2, 9)}-${Date.now()}`;
}

const nuxtApp = useNuxtApp();
const videoId = ref<string | null>(null);
const videoElem = ref<HTMLVideoElement | null>(null);
const isPlaying = ref(false);
const page = usePage();
// Find the poster image in the Kirby image data using the UUID
const posterImage = page.value?.images?.find(
  ({ uuid }: KirbyImage) => uuid === props?.block?.content.videoposter?.[0],
);

const playVideo = () => {
  // Pause all other videos on the page
  const allVideos = document.querySelectorAll("video");
  allVideos.forEach((video) => {
    if (videoElem.value && !video.paused) {
      video.pause();
    }
  });

  // Get the correct video element based on the current `videoId`
  const currentVideoWrapper = document.querySelector(`#${videoId.value}`);
  if (currentVideoWrapper) {
    const currentVideo = currentVideoWrapper.querySelector("video");
    if (currentVideo) {
      currentVideo
        .play()
        .then(() => {
          isPlaying.value = true;
          videoElem.value = currentVideo; // Update videoElem to the correct video
          // Add 'video-playing' class to the wrapper
          currentVideoWrapper.classList.add("video-playing");
          console.log("Video playing:", isPlaying.value);
        })
        .catch((error) => {
          console.error("Error playing video:", error);
        });
    } else {
      console.error(
        "No video element found inside the wrapper with id",
        videoId.value,
      );
    }
  } else {
    console.error("No video wrapper found with the id:", videoId.value);
  }
};
const pauseVideo = () => {
  if (videoElem.value) {
    videoElem.value.pause();
    isPlaying.value = false;
    // Remove 'video-playing' class from the wrapper
    const currentVideoWrapper = document.querySelector(`#${videoId.value}`);
    if (currentVideoWrapper) {
      currentVideoWrapper.classList.remove("video-playing");
    }
    console.log("Video paused:", isPlaying.value);
  } else {
    console.error("No video element found to pause");
  }
};

const runVideoAnimation = () => {
  if (props.block?.content.options === "playOnScroll") {
    nextTick(() => {
      const currentVideoWrapper = document.querySelector(`#${videoId.value}`);
      console.log(`Looking for videoDiv with ID: ${videoId.value}`); // Log the ID being searched for
      if (currentVideoWrapper) {
        console.log("Found video wrapper:", currentVideoWrapper);
        const videoElem = currentVideoWrapper.querySelector("video");
        if (videoElem) {
          ScrollTrigger.create({
            trigger: currentVideoWrapper,
            start: "bottom 90%",
            end: "bottom 10%",
            onEnter: () => videoElem.play(),
            onEnterBack: () => videoElem.play(),
            onLeave: () => videoElem.pause(),
            onLeaveBack: () => videoElem.pause(),
            markers: false,
          });
        } else {
          console.error(
            "No video element found inside video wrapper with id",
            videoId.value,
          );
        }
      } else {
        console.error("No video wrapper found with the id", videoId.value);
      }
    });
  }
};

onMounted(() => {
  videoId.value = generateUniqueId();
  nuxtApp.hook("page:finish", () => {
    nextTick(() => {
      setTimeout(() => {
        runVideoAnimation();
      }, 1950);
    });
  });
});
</script>

<template>
  <div v-if="block?.content.options === 'controls'">
    <div
      :id="videoId"
      class="page-vimeo-wrapper"
      :class="[block?.content.aspectratio, { 'video-playing': isPlaying }]"
    >
      <div class="controls">
        <svg
          v-if="!isPlaying"
          class="play-btn"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 64 64"
          @click.prevent="playVideo"
        >
          <circle cx="32" cy="32" r="32" />
          <polygon points="24,16 24,48 48,32" />
        </svg>
        <svg
          v-if="isPlaying"
          class="pause-btn"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 64 64"
          @click.prevent="pauseVideo"
        >
          <circle cx="32" cy="32" r="32" />
          <rect x="20" y="16" width="8" height="32" />
          <rect x="36" y="16" width="8" height="32" />
        </svg>
      </div>
      <video ref="videoElem" playsinline loop :poster="posterImage?.url">
        <source :src="block?.content.url" type="video/mp4" />
      </video>
    </div>
  </div>
  <div v-if="block?.content.options === 'autoplay'">
    <div
      :id="videoId"
      class="page-vimeo-wrapper"
      :class="block?.content.aspectratio"
    >
      <video playsinline muted autoplay loop :poster="posterImage?.url">
        <source :src="block?.content.url" type="video/mp4" />
      </video>
    </div>
  </div>
  <div v-if="block?.content.options === 'playOnScroll'">
    <div class="page-vimeo-wrapper" :class="block?.content.aspectratio">
      <video
        :id="videoId"
        ref="videoElem"
        playsinline
        muted
        loop
        :poster="posterImage?.url"
        class="scroll-video"
      >
        <source :src="block?.content.url" type="video/mp4" />
      </video>
    </div>
  </div>
  <p v-if="block?.content.caption" class="caption">
    {{ block?.content.caption }}
  </p>
</template>
